
import ShareNewsButton from './ShareNewsButton';
import PrintButton from './PrintButton';

interface Article {
  id: string;
  title: string;
  category: string;
  source: string;
  excerpt: string;
  summary?: string;
  url: string;
  date: string;
}

export default function ArticleContent({ article }: { article: Article }) {
  const content = article.summary || article.excerpt;

  // Split content into paragraphs for better readability
  const paragraphs = content.split('. ').reduce<string[]>((acc, sentence, idx, arr) => {
    const lastGroup = acc[acc.length - 1] || '';
    const combined = lastGroup ? lastGroup + '. ' + sentence : sentence;
    if (combined.length > 400 && lastGroup) {
      acc[acc.length - 1] = lastGroup + '.';
      acc.push(sentence);
    } else {
      acc[acc.length - 1] = combined;
    }
    if (idx === arr.length - 1 && !acc[acc.length - 1].endsWith('.')) {
      acc[acc.length - 1] = acc[acc.length - 1] + '.';
    }
    return acc;
  }, ['']);

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Main Content */}
        <article className="lg:col-span-8">
          <div className="prose prose-lg max-w-none">
            {paragraphs.map((p, idx) => (
              <p key={idx} className="text-gray-700 text-[15px] leading-[1.85] mb-5">
                {idx === 0 && (
                  <span className="text-gray-900 font-semibold text-lg leading-[1.85]">
                    {p.split(' ').slice(0, 6).join(' ')}{' '}
                  </span>
                )}
                {idx === 0 ? p.split(' ').slice(6).join(' ') : p}
              </p>
            ))}
          </div>

          {/* Share & Actions */}
          <div className="mt-8 flex items-center justify-between border-t border-gray-100 pt-8">
            <div className="flex items-center gap-3">
              <ShareNewsButton article={article} />
              <PrintButton article={article} />
            </div>
            <div className="flex items-center gap-3 text-sm text-gray-400">
              <i className="ri-calendar-line"></i>
              <span>Published {new Date(article.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
          </div>
        </article>

        {/* Sidebar */}
        <aside className="lg:col-span-4">
          {/* Category Card */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-6">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">Category</h3>
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-teal-50 text-teal-700 rounded-lg text-sm font-medium">
              <i className="ri-price-tag-3-line"></i>
              {article.category}
            </span>
          </div>

          {/* Quick Links */}
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-4">Quick Links</h3>
            <div className="space-y-2">
              <a href="/news" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-newspaper-line text-gray-600"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">All Local News</span>
              </a>
              <a href="/calendar" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-calendar-event-line text-gray-600"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">Events Calendar</span>
              </a>
              <a href="/directory" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                  <i className="ri-building-line text-gray-600"></i>
                </div>
                <span className="text-sm font-medium text-gray-700">Business Directory</span>
              </a>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
